import { isGaoqianBillingEnabled } from '../../services/extension/gaoqian/billing';

/** 高潜生产域名：即使构建未带 VITE 开关，登录/侧栏仍显示高潜品牌（不影响华为等通用环境） */
function isGaoqianBrandingHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const h = window.location.hostname.toLowerCase();
  return h === 'crm.hi-po.com.cn' || h === '39.107.92.97';
}

function useGaoqianBranding(): boolean {
  return isGaoqianBillingEnabled() || isGaoqianBrandingHost();
}

export interface LoginBranding {
  logoSrc: string;
  logoAlt: string;
  title: string;
  subtitle: string;
  tagline: string;
  footer: string;
}

const ZHIYINGYUN_LOGO_SRC = '/brand/zhiyingyun-nexuscrm-logo-horizontal.png';
const ZHIYINGYUN_LOGO_WHITE_SRC = '/brand/zhiyingyun-nexuscrm-logo-horizontal-white.png';

const DEFAULT_BRANDING: LoginBranding = {
  logoSrc: ZHIYINGYUN_LOGO_SRC,
  logoAlt: '智赢云',
  title: '',
  subtitle: '全业务管理系统',
  tagline: '管理培训与咨询行业专用',
  footer: '智赢云--专为管理培训与咨询公司打造的管理系统！ © 2024',
};

const GAOQIAN_BRANDING: LoginBranding = {
  logoSrc: '/extension/gaoqian/logo.svg',
  logoAlt: '高潜咨询',
  title: '高潜CRM',
  subtitle: 'CRM 管理系统',
  tagline: '助力中国高潜企业加速发展',
  footer: '高潜（北京）咨询有限公司 © HIPO',
};

export interface AppBranding {
  /** 浏览器标签页 <title> */
  documentTitle: string;
  productName: string;
  metaDescription: string;
  appleWebAppTitle: string;
}

const DEFAULT_APP: AppBranding = {
  documentTitle: '智赢云CRM - 客户管理系统',
  productName: '智赢云CRM',
  metaDescription: '智赢云CRM - 一站式企业CRM解决方案',
  appleWebAppTitle: '智赢云CRM',
};

const GAOQIAN_APP: AppBranding = {
  documentTitle: '高潜CRM - 客户管理系统',
  productName: '高潜CRM',
  metaDescription: '高潜CRM - 高潜咨询客户管理系统',
  appleWebAppTitle: '高潜CRM',
};

/** 浏览器标签、PWA 元数据等全局产品名 */
export function getAppBranding(): AppBranding {
  return useGaoqianBranding() ? GAOQIAN_APP : DEFAULT_APP;
}

/** 高潜构建时覆盖 index.html 里的默认「智赢云」标题 */
export function applyAppBrandingToDocument(): void {
  const b = getAppBranding();
  document.title = b.documentTitle;
  const desc = document.querySelector('meta[name="description"]');
  if (desc) desc.setAttribute('content', b.metaDescription);
  const apple = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (apple) apple.setAttribute('content', b.appleWebAppTitle);
}

/** 登录页品牌：高潜扩展开启时使用高潜 LOGO 与文案 */
export function getLoginBranding(): LoginBranding {
  return useGaoqianBranding() ? GAOQIAN_BRANDING : DEFAULT_BRANDING;
}

export interface SidebarBranding {
  /** 展开态 LOGO 图（深色侧边栏适配的浅色版）；为空时回退到 expandedText 文字 */
  logoSrc: string;
  logoAlt: string;
  /** 无 LOGO 图或默认租户时的展开文字 */
  expandedText: string;
  /** 收起态显示的紧凑文字 */
  collapsedText: string;
}

const DEFAULT_SIDEBAR: SidebarBranding = {
  logoSrc: ZHIYINGYUN_LOGO_WHITE_SRC,
  logoAlt: '智赢云',
  expandedText: '智赢云',
  collapsedText: 'CRM',
};

const GAOQIAN_SIDEBAR: SidebarBranding = {
  logoSrc: '/extension/gaoqian/logo-light.svg',
  logoAlt: '高潜咨询',
  expandedText: '高潜咨询',
  collapsedText: '高潜',
};

/** 侧边栏顶部品牌：高潜扩展开启时用高潜浅色 LOGO，否则用默认文字 */
export function getSidebarBranding(): SidebarBranding {
  return useGaoqianBranding() ? GAOQIAN_SIDEBAR : DEFAULT_SIDEBAR;
}
